import React, { useEffect } from "react";
import { KButton, KIcon } from "@kyburz/kdesign-system";
import { makeStyles, Typography } from "@material-ui/core";

import useSetState from "../../../../hooks/useTypedSetState";
import {
  ChangableVehicleTemplate,
  VehicleTemplate,
} from "../../facades/VehicleTemplate";
import { useGetDefaultVehicleTemplates } from "../../requests/GetDefaultTempalte";

import TableIcon from "../../../../assets/icons/Table";
import ToggleButton from "../ToggleButton";
import TableEditor from "./modes/TableEditor";
import JSONEditor from "../../../../components/JSONEditor/JSONEditor";
import { RawVehicleTemplate } from "../../typings/RawVehicleTemplate";

const useStyles = makeStyles(() => ({
  title: {
    marginBottom: "4px",
  },
  headerButtons: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  applyDefault: { minWidth: 211 },
}));

const defaultTemplateParams = {};

enum CreatingMode {
  Json = "json",
  Table = "table",
}

const toggleStates = [
  {
    label: "Json mode",
    value: CreatingMode.Json,
    icon: <KIcon icon="file-text" size={24} />,
  },
  {
    label: "Table mode",
    value: CreatingMode.Table,
    icon: <TableIcon />,
  },
];

type TemplateEditorProps = {
  template: ChangableVehicleTemplate;
  setTemplate: (template: Partial<ChangableVehicleTemplate>) => void;
};

const TemplateEditor: React.FC<TemplateEditorProps> = ({
  template,
  setTemplate,
}) => {
  const classes = useStyles();

  const [state, setState] = useSetState({
    creatingMode: CreatingMode.Json,
  });
  // JSON template is stored separately as it's raw data - types are different
  const [jsonTemplate, setJsonTemplate] = useSetState<
    Partial<RawVehicleTemplate>
  >({});

  const onJsonChange = (json: string) => {
    try {
      JSON.parse(json);
    } catch (e) {
      return;
    }
    const {
      name,
      description,
      isDefaultTemplate,
      displayColumns,
      ignitionValue,
      columns,
      tags,
      owner,
      companies,
      errorCodesMappingName,
      internalTariffId,
      odometerSource,
    } = new VehicleTemplate(JSON.parse(json));
    setTemplate({
      name,
      description,
      isDefaultTemplate,
      displayColumns,
      ignitionValue,
      columns,
      tags,
      owner,
      companies,
      errorCodesMappingName,
      internalTariffId,
      odometerSource,
    });
  };

  const currentEditorMode = {
    [CreatingMode.Json]: (
      <JSONEditor value={jsonTemplate} jsonChanged={onJsonChange} />
    ),
    [CreatingMode.Table]: (
      <TableEditor
        name={template.name}
        isDefaultTemplate={template.isDefaultTemplate}
        ignitionValue={template.ignitionValue}
        description={template.description}
        columns={template.columns}
        displayColumns={template.displayColumns}
        tags={template.tags}
        owner={template.owner}
        errorCodesMappingName={template.errorCodesMappingName}
        internalTariffId={template.internalTariffId}
        odometerSource={template.odometerSource}
        onTemplateChanged={(changed) => setTemplate({ ...changed })}
      />
    ),
  }[state.creatingMode];

  useEffect(() => {
    if (state.creatingMode === CreatingMode.Json) {
      const defaultColumns = ["datetime"]
        .concat(template.ignitionValue ? [template.ignitionValue] : [])
        .concat(
          template.columns
            .filter(
              (column) =>
                column.isDefault &&
                column.technicalName !== "datetime" &&
                column.technicalName !== template.ignitionValue,
            )
            .map(({ technicalName }) => technicalName),
        );

      const digital = template.columns
        .filter((column) => column.isDigital)
        .map(({ technicalName }) => technicalName);

      const newTemplate = {
        name: template.name,
        description: template.description,
        ignition_value: template.ignitionValue,
        is_default_template: template.isDefaultTemplate,
        display_columns: template.displayColumns,
        all_columns: template.columns.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.technicalName]: {
              name: curr.name,
              hover: curr.hover,
              has_hover: curr.hasHover,
              default: curr.default,
              unit: curr.unit,
              scale_factor: curr?.scale_factor,
              digits_after_comma: curr?.digits_after_comma,
              values_mapping_name: curr?.values_mapping_name,
              type: curr?.type,
            },
          }),
          {},
        ),
        default_columns: defaultColumns,
        is_digital: digital,
        companies: template.companies,
        tags: template.tags,
        owner: template.owner,
        error_codes_mapping_name: template.errorCodesMappingName,
        internal_tariff_id: template.internalTariffId,
        odometer_source: template.odometerSource,
      };
      setJsonTemplate(newTemplate);
    }
  }, [setState, state.creatingMode, template]);

  const { loading: defaultTemplateLoading, request: getDefaultTemplate } =
    useGetDefaultVehicleTemplates(defaultTemplateParams);

  const onCopyDefaultTemplate = async () => {
    const defaultTemplate = await getDefaultTemplate();
    setTemplate({ ...defaultTemplate, isDefaultTemplate: false });
  };

  return (
    <>
      <Typography variant="h5" className={classes.title}>
        Editor Mode
      </Typography>

      <div className={classes.headerButtons}>
        <ToggleButton
          states={toggleStates}
          currectState={state.creatingMode}
          onChangeState={(creatingMode) => setState({ creatingMode })}
        />

        <KButton
          children="Apply Default config"
          theme="primary"
          size="small"
          className={classes.applyDefault}
          loading={defaultTemplateLoading}
          onClick={onCopyDefaultTemplate}
        />
      </div>

      {currentEditorMode}
    </>
  );
};

export default TemplateEditor;
